import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { onNetworkFailure } from '../api/client';

/**
 * "Il server è raggiungibile?"
 *
 * PietroChip non funziona offline e non finge di farlo: quando la connessione
 * cade, le scritture vengono **bloccate** e l'interfaccia lo dice a chiare
 * lettere. La ragione è pratica: davanti a una fila di atleti, un pulsante che
 * sembra aver funzionato e invece non ha scritto niente è il modo peggiore di
 * fallire — il chip risulta restituito solo nella testa dell'operatore.
 *
 * Tre sorgenti, in ordine di prontezza:
 * 1. `navigator.onLine` e gli eventi `online`/`offline` del sistema;
 * 2. il fallimento immediato di una richiesta vera (l'operatore ha premuto un
 *    pulsante e non è partita): è la più rapida e la più onesta;
 * 3. un controllo di salute periodico, che è l'unico modo per accorgersi che la
 *    rete c'è ma il server no.
 */

const HEALTH_PATH = '/api/health';
const POLL_ONLINE_MS = 20_000;
const POLL_OFFLINE_MS = 3_000;

export type ConnectionContextValue = {
  online: boolean;
  /** Segnala che una scrittura non è partita perché la rete non c'è. */
  markOffline: () => void;
  /** Riprova subito, senza aspettare il prossimo controllo. */
  recheck: () => Promise<boolean>;
};

const ConnectionContext = createContext<ConnectionContextValue | null>(null);

async function ping(): Promise<boolean> {
  try {
    const response = await fetch(HEALTH_PATH, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  }
}

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(() => navigator.onLine);

  const check = useCallback(async () => {
    {
      const reachable = await ping();
      setOnline(reachable);
      return reachable;
    }
  }, []);

  // Una richiesta vera fallita è la notizia più fresca che possiamo avere.
  useEffect(() => onNetworkFailure(() => setOnline(false)), []);

  useEffect(() => {
    function handleOnline() {
      void check();
    }
    function handleOffline() {
      setOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [check]);

  /**
   * Controllo periodico. Quando siamo online è raro e silenzioso; quando siamo
   * offline è frequente, perché è così che ci si accorge del ritorno della rete
   * anche se il sistema operativo non emette nulla (succede spesso con il
   * roaming fra ripetitori).
   */
  const onlineRef = useRef(online);
  onlineRef.current = online;

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function tick() {
      // A schermo spento non si consuma traffico per un'informazione che
      // nessuno sta guardando: al risveglio si ricontrolla subito.
      if (document.visibilityState === 'visible' || !onlineRef.current) {
        await check();
      }
      if (cancelled) return;
      timer = window.setTimeout(tick, onlineRef.current ? POLL_ONLINE_MS : POLL_OFFLINE_MS);
    }

    timer = window.setTimeout(tick, onlineRef.current ? POLL_ONLINE_MS : POLL_OFFLINE_MS);

    function handleVisibility() {
      if (document.visibilityState === 'visible') void check();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [check]);

  const value = useMemo<ConnectionContextValue>(
    () => ({
      online,
      markOffline: () => setOnline(false),
      recheck: check,
    }),
    [online, check],
  );

  return <ConnectionContext.Provider value={value}>{children}</ConnectionContext.Provider>;
}

export function useConnection(): ConnectionContextValue {
  const context = useContext(ConnectionContext);
  if (!context) throw new Error('useConnection va usato dentro ConnectionProvider');
  return context;
}

/**
 * Avviso a tutto schermo quando il server non risponde.
 *
 * Copre l'interfaccia invece di limitarsi a una striscia in alto: se resta
 * cliccabile, prima o poi qualcuno ci clicca.
 */
export function OfflineOverlay() {
  const { online, recheck } = useConnection();
  if (online) return null;

  return (
    <div className="offline" role="alertdialog" aria-modal="true" aria-labelledby="offline-title">
      <div className="offline__box">
        <div className="offline__pulse" aria-hidden="true" />
        <h2 id="offline-title" style={{ fontSize: '1.25rem' }}>
          Connessione assente
        </h2>
        <p className="muted">
          I dati dei chip sono sul server: finché la connessione non torna non è possibile
          registrare restituzioni. I campi già compilati non vanno persi.
        </p>
        <button type="button" className="btn btn--primary btn--lg" onClick={() => void recheck()}>
          Riprova ora
        </button>
      </div>
    </div>
  );
}
